/**
 * Card-combat enemy templates (HEX-51 / ADR-0015 §4; flattened in HEX-60 once
 * the V1 `Enemy` registry retired).
 *
 * Each enemy is structurally symmetric with the player: a grid of cards + a dice
 * pool + a status bag. A template is the pure-data spec (tier, hp, loadout,
 * drops); `buildEnemy` instantiates a runtime `Combatant` with fresh per-instance
 * maps.
 */

import { createStatusBag } from '../status';
import type { CombatDrops, Combatant, EnemyTier, GridPlacement } from '../types';

/** Per-tier grid footprint and dice count (ADR-0015 §4). Authoring stays terse:
 * a template names its tier, the build derives pool size, the test asserts the
 * loadout fits the tier grid. Dimensions are `{ rows, cols }`. */
export const TIER_GRID: Record<EnemyTier, { rows: number; cols: number }> = {
  common: { rows: 2, cols: 2 },
  rare: { rows: 3, cols: 3 },
  epic: { rows: 4, cols: 3 },
  legendary: { rows: 4, cols: 4 },
};

/** Dice rolled per turn by tier (ADR-0015 §4). Enemies carry no `die` cards in
 * V1, so this is their whole pool. */
export const TIER_DICE: Record<EnemyTier, number> = {
  common: 2,
  rare: 3,
  epic: 4,
  legendary: 5,
};

export type EnemyTemplate = {
  id: string;
  name: string;
  tier: EnemyTier;
  hp: number;
  /** Single BMP codepoint shown prominently in the combat enemy band (HEX-69),
   * so an enemy reads at a glance rather than as a bare name. Monochrome, common
   * system-font glyph; kept distinct from the tile glyphs (HEX-66) and the die /
   * status icons so the combat surface never reuses a meaning. */
  glyph: string;
  loadout: GridPlacement[];
  /** Placeholder economy drop awarded on player victory (HEX-39 / ADR-0012 §2).
   * Ported from the V1 per-enemy values at the HEX-60 retirement. */
  drops: CombatDrops;
};

/** Instantiate a fresh runtime `Combatant` from a template. `basePoolSize` is the
 * tier dice count; the loadout `capacity` is the tier grid (§4, §18) — the only
 * grid (ADR-0022). `aiProfile` is the tier itself (ADR-0016): the encounter
 * tier and the AI ladder rung are one and the same, so a legendary enemy fights
 * like a legendary. Each call gets its own `trapState` / `frozenCards` / fired
 * set so concurrent encounters never share mutable runtime. */
export function buildEnemy(template: EnemyTemplate): Combatant {
  return {
    id: template.id,
    name: template.name,
    hp: template.hp,
    maxHp: template.hp,
    aiProfile: template.tier,
    inventory: {
      loadout: { placements: template.loadout, capacity: { ...TIER_GRID[template.tier] } },
    },
    dicePool: [],
    basePoolSize: TIER_DICE[template.tier],
    statusBag: createStatusBag(),
    block: 0,
    trapState: new Map(),
    frozenCards: new Map(),
    oncePerCombatFired: new Set(),
    oncePerTurnFired: new Set(),
  };
}
